// ADR 5.9.26u §3 — the medium chrome: a vertical rail down the leading edge.
//
// Rendered when `navChrome(width)` is `rail` and `windowSizeClass(width)` is
// `medium`. Icons with their labels *below* them, which is what fits in the
// width a tablet in portrait has to spare — `Sidebar.tsx` is the same `rail`
// verdict drawn with a desk's horizontal room. The table has two values and so
// does the web; the sidebar is not a third.
//
// ADR 15.9.26ah §3 — Material 3's navigation rail: `w-20` (the 80 its `w-16`
// items in a `p-2` list already composed, made explicit), the icon in the same
// active pill the bar draws, `title` on every item (this was the one drawing
// without it) and `FOCUS_RING`. No FAB: the roadmap's first draft named one and
// §3 declines it — a second link to `/tutor` above the Tutor destination is new
// chrome, and Android's `TutorFab.kt` is page-scaffold, not rail.

import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'

import { Button, FOCUS_RING } from '../../ui/Button'
import { Icon } from '../../ui/icons'
import { SoonNavItem } from './SoonNavItem'
import type { Destination } from './destinations'

/**
 * ADR 8.9.26i §4 — the return trip.
 *
 * `onExpand` is present only when this rail is standing in for a *collapsed*
 * sidebar; at the width where the rail is the table's own answer there is
 * nothing to expand into, so no control renders. A one-way collapse would
 * satisfy a carelessly-written criterion and strand the learner (AC6). Like
 * `Sidebar.tsx`, this file may not spell a size-class string — the boolean
 * arrives as a callback that is either there or not. It stays a ghost `Button`
 * reading `tutor_expand`: the icon set has no glyph that says "expand" without
 * also saying "back", and AC6 clicks it by that name.
 */
export function Rail({ items, onExpand }: { items: Destination[]; onExpand?: () => void }) {
  const { t } = useTranslation()
  return (
    <nav data-testid="nav-rail" className="w-20 border-e border-boundary bg-surface">
      {onExpand === undefined ? null : (
        <div className="flex justify-center p-2">
          <Button variant="ghost" onClick={onExpand}>
            {t('tutor_expand')}
          </Button>
        </div>
      )}
      <ul className="flex flex-col gap-2 p-2">
        {items.map((item) => (
          <li key={item.path}>
            <RailItem item={item} />
          </li>
        ))}
      </ul>
    </nav>
  )
}

const RAIL_ITEM = 'group flex w-16 flex-col items-center gap-1 rounded-control py-2 text-label-md'

/**
 * ADR 30.9.26s §3 — one rail item: a link, or a reserved destination drawn
 * disabled. Extracted because `Rail` was at its 50-line budget.
 */
function RailItem({ item }: { item: Destination }) {
  const { t } = useTranslation()
  if (item.soon) return <SoonNavItem item={item} className={RAIL_ITEM} />
  return (
    <NavLink
      to={item.path}
      end={item.end}
      title={t(item.labelKey)}
      className={({ isActive }) =>
        `${RAIL_ITEM} ${FOCUS_RING} motion-safe:transition-colors duration-short ${
          isActive ? 'font-medium text-ink' : 'text-ink-muted'
        }`
      }
    >
      {({ isActive }) => (
        <>
          <span
            className={`flex h-8 w-16 items-center justify-center rounded-full ${
              isActive ? 'bg-secondary-soft text-on-secondary-soft' : 'group-hover:bg-surface-sunken'
            }`}
          >
            <Icon name={item.icon} />
          </span>
          {/* No `truncate` here: the 64px item lets the two 69px labels
              (`de` Karteikarten, `fi` Muistikortit) overhang by 2–3px a
              side, centred, as they did before this ADR — the bar
              truncates because its slot is the viewport's, this one is
              not. */}
          <span>{t(item.labelKey)}</span>
        </>
      )}
    </NavLink>
  )
}
